import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { Map } from 'immutable'
import PropTypes from 'prop-types'
import FormsyForm from 'formsy-react'
import { connect as connectRedux } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _get from 'lodash/get'
import _findIndex from 'lodash/findIndex'
import _debounce from 'lodash/debounce'
import {
  getSessionStorage,
  LOCAL_PREFERENCES_EMAIL_SIGNUP_ADDRESS,
} from '../../services/local-preferences/index'
import { getBoundActions } from '../../actions/index'
import {
  getPasswordValidation,
} from '../../services/password/index'
import { ERROR_EMAIL_INVALID } from '../../services/email-signup/index'
import { getLoginAssistanceUrl } from '../../services/url/index'
import { getPrimary } from '../../services/languages/index'
import { TYPE_LOGIN } from '../../services/dialog/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import {
  FORM_INPUT_NAME_FIRST_NAME,
  FORM_INPUT_NAME_LAST_NAME,
  FORM_INPUT_NAME_EMAIL,
  FORM_INPUT_NAME_PASSWORD,
  FORM_INPUT_NAME_EMAIL_OPT_IN,
  FORM_INPUT_NAME_TERMS,
} from '../../services/form/index'
import { TARGET_BLANK } from '../Link/constants'
import { H3, HEADING_TYPES } from '../Heading/index'
import Button, { TYPES } from '../Button/index'
import Sherpa from '../Sherpa/index'
import Link from '../Link/index'
import {
  TextInput, EmailInput, Password, Checkbox, CHECKBOX_STYLES,
} from '../FormInput.v2/index'
import CartBillingProcessing from '../CartBillingProcessing/index'
import { get as getConfig } from '../../config/index'
import { US, GDPR_COUNTRIES } from '../../services/country/index'
import PrivacyTerms from './PrivacyTerms'

const config = getConfig()

function AccountCreateForm (props) {
  const {
    onFormSubmit,
    submitText,
    isProcessing,
    hideLogin,
    location,
    checkout,
    getUserEmailAvailability,
    resetUserEmailAvailability,
    resetUserChildAccount,
    setOverlayDialogVisible,
    resetCheckoutEmailCaptureStatus,
    setCheckoutAccountValid,
    setCheckoutLoginData,
    user,
    auth,
    staticText,
  } = props
  const [formError, setFormError] = useState(false)
  const [firstNameValue, setFirstNameValue] = useState(checkout.getIn(['account', 'firstName'], ''))
  const [lastNameValue, setLastNameValue] = useState(checkout.getIn(['account', 'lastName'], ''))
  const [emailValue, setEmailValue] = useState(checkout.getIn(['account', 'email'], ''))
  const [passwordValue, setPasswordValue] = useState(checkout.getIn(['account', 'password'], ''))
  const [emailOptInValue, setEmailOptInValue] = useState(checkout.getIn(['account', 'emailOptIn'], false))
  const [termsValue, setTermsValue] = useState(checkout.getIn(['account', 'terms'], false))
  const [termsInitialChecked, setTermsInitialChecked] = useState(false)
  const [shouldShowLoginModal, setShouldShowLoginModal] = useState(true)
  const [initialRender, setInitialRender] = useState(true)

  const isLoggedIn = getAuthIsLoggedIn(auth)
  const isChildAccount = user.get('childProfile') === true
  const emailUnavailable = user.get('emailAvailable') === false
  const hideFormFields = !isLoggedIn && (isChildAccount || emailUnavailable)
  const language = user.getIn(['data', 'language'])
  const userCreateProcessing = user.get('userCreateProcessing')
  const userCreateError = user.getIn(['newUser', 'data', 'success']) === false
  const userProcessing = user.get('processing')
  const sessionPreferences = getSessionStorage('gaia', {}) || {}
  const sessionEmailValue = _get(sessionPreferences, [-1, LOCAL_PREFERENCES_EMAIL_SIGNUP_ADDRESS]) || ''
  const country = user.getIn(['data', 'requestCountry'], config.appCountry)
  const emailOptIn = checkout.getIn(['account', 'emailOptIn'], country === US)
  const isGdprCountry = _findIndex(GDPR_COUNTRIES, (c) => c === country) > -1

  const submitDisabled = useMemo(() => {
    if (isLoggedIn) {
      return !checkout.get('accountValid')
    }
    const processing = user.get('emailAvailabilityProcessing')
    const available = user.get('emailAvailable')
    const emailCaptureErrorCode = checkout.get('emailCaptureErrorCode')
    return (
      !checkout.get('accountValid')
      || !available
      || emailCaptureErrorCode
      || processing
    )
  }, [isLoggedIn, checkout, user])

  const debounceSetFormValid = useCallback(_debounce((valid) => {
    if (valid !== checkout.get('accountValid')) {
      setCheckoutAccountValid(valid)
    }
  }, 100), [checkout])

  const handleOnBlurCheckEmail = (value) => {
    const pattern = /^.+@.+\..{2,}$/

    // We are compensating for Formsy saying the email is valid (when its not)
    if (!pattern.test(value)) {
      return
    }

    getUserEmailAvailability(value)
  }

  const handleOnChangeFirstName = (value) => {
    setFirstNameValue(value)
  }

  const handleOnChangeLastName = (value) => {
    setLastNameValue(value)
  }

  const handleOnChangeEmail = (value) => {
    setEmailValue(value)

    // hack for browser autofill to trigger for checking email
    if (!emailValue && value) {
      handleOnBlurCheckEmail(value)
    }

    if (user.has('emailAvailable')) {
      resetUserEmailAvailability()
    }

    if (user.has('childProfile')) {
      resetUserChildAccount()
    }

    if (checkout.has('emailCaptureErrorCode')) {
      resetCheckoutEmailCaptureStatus()
    }

    // reset the login modal flag
    if (!shouldShowLoginModal) {
      setShouldShowLoginModal(true)
    }
  }

  const handlePasswordChange = (value) => {
    setPasswordValue(value)
  }

  const handleOnChangeTerms = (_, checked) => {
    setTermsValue(checked)
    if ((!termsInitialChecked && checked) || (!termsInitialChecked && !checked)) {
      setTermsInitialChecked(true)
    }
  }

  const handleOnChangeEmailOptIn = (_, checked) => {
    setEmailOptInValue(checked)
  }

  const onClickLoginButton = () => {
    setOverlayDialogVisible(TYPE_LOGIN)
  }

  const onValidSubmit = (data) => {
    onFormSubmit(data)
    setFormError(false)
  }

  const validateEmail = () => {
    let errorMessage = null
    if (user.get('emailAvailable') === false && !isChildAccount) {
      // show login modal if email is unavailable
      if (shouldShowLoginModal) {
        setCheckoutLoginData(emailValue)
        setOverlayDialogVisible(TYPE_LOGIN)
        setShouldShowLoginModal(false)
      }
      errorMessage = staticText.get('cantUseEmail')
    } else if (checkout.get('emailCaptureErrorCode') === ERROR_EMAIL_INVALID) {
      errorMessage = staticText.get('errorMessageInvalidEmail')
    }
    return errorMessage
  }

  const getFormClassName = () => {
    const baseClassName = ['account-create-form']

    if (formError) {
      baseClassName.push('account-create-form__error')
    }

    if (userCreateProcessing) {
      baseClassName.push('account-create-form--hidden')
    }

    return baseClassName.join(' ')
  }

  useEffect(() => {
    if (!auth.get('jwt') && sessionEmailValue) {
      setEmailValue(sessionEmailValue)
      getUserEmailAvailability(sessionEmailValue)
    }
  }, [])

  useEffect(() => {
    setEmailOptInValue(emailOptIn)
  }, [emailOptIn])

  useEffect(() => {
    if (initialRender) {
      // only fire once on initial render
      setInitialRender(false)
    } else {
      setFormError(submitDisabled)
    }
  }, [submitDisabled])

  return (
    /* eslint-disable jsx-a11y/tabindex-no-positive */
    <>
      {
        userCreateError ? (
          <p className="account-create-form__error-message">
            {staticText.get('userCreateError')}
          </p>
        )
          : null
      }
      {
        userProcessing && !isProcessing ? (
          <Sherpa className={['account-create-form__sherpa']} />
        )
          : null
      }
      {
        isProcessing ? (
          <CartBillingProcessing alignLeft />
        )
          : null
      }
      {
        !isProcessing && !userProcessing ? (
          <FormsyForm
            onValid={() => debounceSetFormValid(true)}
            onInvalid={() => debounceSetFormValid(false)}
            onValidSubmit={onValidSubmit}
            className={getFormClassName()}
            noValidate
          >
          {!auth.get('jwt')
            ? <>
              {
                userCreateProcessing ? (
                  <CartBillingProcessing />
                )
                  : null
              }
              <div className="account-create-form__inline-container">
                <TextInput
                  name={FORM_INPUT_NAME_FIRST_NAME}
                  validations={{ isSpecialWords: true }}
                  validationErrors={{
                    isSpecialWords: staticText.get('errorFirstName'),
                  }}
                  validationTimeout={750}
                  label={staticText.get('firstName')}
                  maxLength={32}
                  onChange={handleOnChangeFirstName}
                  value={firstNameValue || checkout.getIn(['account', 'firstName'], '')}
                  tabIndex="1"
                  required
                  autocomplete="given-name"
                />
                <TextInput
                  validations={{ isSpecialWords: true }}
                  validationErrors={{
                    isSpecialWords: staticText.get('errorLastName'),
                  }}
                  validationTimeout={750}
                  label={staticText.get('lastName')}
                  name={FORM_INPUT_NAME_LAST_NAME}
                  maxLength={32}
                  onChange={handleOnChangeLastName}
                  value={lastNameValue || checkout.getIn(['account', 'lastName'], '')}
                  tabIndex="2"
                  required
                  autocomplete="family-name"
                />
              </div>
              <EmailInput
                className="account-create-form__email"
                disabled={user.get('emailAvailabilityProcessing') === true}
                validations={{ isEmail: true }}
                validationErrors={{
                  isEmail: staticText.get('emailValidError'),
                }}
                validationTimeout={sessionEmailValue ? 0 : 750}
                label={staticText.get('email')}
                onBlur={handleOnBlurCheckEmail}
                onChange={handleOnChangeEmail}
                name={FORM_INPUT_NAME_EMAIL}
                value={emailValue}
                error={validateEmail()}
                tabIndex="3"
                required
                autocomplete="email"
              />
              {
                emailUnavailable && !isChildAccount
                  ? <div className="account-create-form__email-exists">
                    <div className="account-create-form__email-exists-error">
                      {`${staticText.get('cantUseEmail')} `}
                    </div>
                    <Button
                      type={TYPES.LINK}
                      className="account-create-form__email-exists-login-button"
                      onClick={onClickLoginButton}
                    >
                      {staticText.get('logIn')}
                    </Button>
                  </div>
                  : null
              }
              { isChildAccount
                ? <div className="account-create-form__email-child-error">
                  {`${staticText.get('customerServiceStart')} `}
                  <Link
                    directLink
                    to={getLoginAssistanceUrl(getPrimary(language))}
                    target={TARGET_BLANK}
                  >
                    {`${staticText.get('customerServiceLink')} `}
                  </Link>
                  {staticText.get('customerServiceEnd')}
                </div>
                : null
              }
              <div className={`account-create-form__password-wrapper${hideFormFields ? ' account-create-form__password-wrapper--hidden' : ''}`}>
                <Password
                  className="account-create-form__password"
                  validations={getPasswordValidation()}
                  validationErrors={{
                    minLength: staticText.get('passwordError'),
                    matchRegexp: staticText.get('passwordError'),
                    maxLength: staticText.get('passwordMaxLengthMessage'),
                  }}
                  validationTimeout={750}
                  name={FORM_INPUT_NAME_PASSWORD}
                  label={staticText.get('createPassword')}
                  onChange={handlePasswordChange}
                  value={passwordValue}
                  showStrength
                  tabIndex="4"
                  showHideTabIndex="5"
                  required
                  autocomplete="new-password"
                />
              </div>
            </>
            : <div className="account-create-form__fields-unentitled">
              <H3 as={HEADING_TYPES.H5} className="account-create-form__field-title">
                {staticText.get('yourGaiaAccount')}
              </H3>
              <p>{staticText.get('agreeTermsActivate')}</p>
              <H3 as={HEADING_TYPES.H5} className="account-create-form__field-title">
                {staticText.get('email')}
              </H3>
              <p>{auth.get('email')}</p>
              <H3 as={HEADING_TYPES.H5} className="account-create-form__field-title">
                {staticText.get('username')}
              </H3>
              <p>{auth.get('username')}</p>
            </div>
          }
          {isGdprCountry
            ? <>
              <Checkbox
                className="account-create-form__email-optin"
                name={FORM_INPUT_NAME_EMAIL_OPT_IN}
                label={staticText.get('emailOptInLabel')}
                value={emailOptInValue}
                hide={hideFormFields}
                tabIndex="6"
                onChange={handleOnChangeEmailOptIn}
                setValueOnKeyUp
              />
              <Checkbox
                className="account-create-form__terms-optin"
                onChange={handleOnChangeTerms}
                label={<PrivacyTerms staticText={staticText} location={location} isGdpr/>}
                style={CHECKBOX_STYLES.PRIMARY}
                validations={{ equals: true }}
                // You have to send in a validation error message to get the checkbox
                // to show a red error outline, but the error message doesn't show
                // Only show the error if the checkbox has been checked and then unchecked
                validationErrors={{
                  equals: termsInitialChecked ? staticText.get('requiredField') : null,
                }}
                name={FORM_INPUT_NAME_TERMS}
                value={termsValue}
                hide={hideFormFields}
                tabIndex="7"
                setValueOnKeyUp
                required
              />
            </>
            : <>
              <Checkbox
                name={FORM_INPUT_NAME_EMAIL_OPT_IN}
                value={true}
                hide={true}
                label=""
              />
              <Checkbox
                name={FORM_INPUT_NAME_TERMS}
                value={true}
                hide={true}
                label=""
              />
              <PrivacyTerms staticText={staticText} location={location} isGdpr={false}/>
            </>
          }
          <div className={`account-create-form__continue-wrapper${isChildAccount ? ' account-create-form__continue-wrapper--hidden' : ''}`}>
            {
              submitDisabled
                ? <Button
                  type={TYPES.PRIMARY}
                  className="account-create-form__continue-button"
                  onClick={() => setFormError(true)}
                  tabIndex="9"
                >
                  {user.get('emailAvailabilityProcessing')
                    ? staticText.get('processing')
                    : submitText || staticText.get('continue')}
                </Button>
                : <Button
                  type={TYPES.PRIMARY}
                  className="account-create-form__continue-button"
                  tabIndex="10"
                  submit
                >
                  {user.get('emailAvailabilityProcessing')
                    ? staticText.get('processing')
                    : submitText || staticText.get('continue')}
                </Button>
            }
            {
              emailUnavailable && !isLoggedIn && !hideLogin
                ? <Button
                  type={TYPES.SECONDARY}
                  className="account-create-form__login-button"
                  onClick={onClickLoginButton}
                >
                  {staticText.get('logIn')}
                </Button>
                : null
            }
          </div>
          {
            formError && !hideFormFields ? (
              <p className="account-create-form__error">
                {staticText.get('complete')}
              </p>
            ) : null
          }
        </FormsyForm>) : null
      }
    </>
    /* eslint-enable jsx-a11y/tabindex-no-positive */
  )
}

AccountCreateForm.propTypes = {
  onFormSubmit: PropTypes.func.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  location: PropTypes.object.isRequired,
  checkout: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  getUserEmailAvailability: PropTypes.func.isRequired,
  resetCheckoutEmailCaptureStatus: PropTypes.func.isRequired,
  resetUserEmailAvailability: PropTypes.func.isRequired,
  resetUserChildAccount: PropTypes.func.isRequired,
  setCheckoutAccountValid: PropTypes.func.isRequired,
  setOverlayDialogVisible: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
  submitText: PropTypes.string,
  hideLogin: PropTypes.bool,
}

export default connectRedux(
  (state) => ({
    auth: state.auth,
    checkout: state.checkout,
    user: state.user,
    userAccount: state.userAccount,
    staticText: state.staticText.getIn(['data', 'accountCreateForm', 'data'], Map()),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      getUserEmailAvailability: actions.user.getUserEmailAvailability,
      resetCheckoutEmailCaptureStatus: actions.checkout.resetCheckoutEmailCaptureStatus,
      resetUserEmailAvailability: actions.user.resetUserEmailAvailability,
      resetUserChildAccount: actions.user.resetUserChildAccount,
      setCheckoutAccountValid: actions.checkout.setCheckoutAccountValid,
      setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
      setCheckoutLoginData: actions.dialog.setCheckoutLoginData,
    }
  },
)(AccountCreateForm)
